// ============================================================
// ARENA SHAPES - the catalogue behind the arena's shape select.
//
// Two UIs offer the same list (the option bar and the terrain editor
// modal), and both used to carry their own copy of it. The catalogue
// lives here, in the engine, beside the boundary it describes - the
// STRUCTURE_SHAPES precedent.
//
// Shape identity is STORED (`Boundary.shape`), not inferred: inference
// by sides+scaleX misreads anything unusual as "Square" and cannot see
// scaleZ at all. Legacy boundaries without the field still infer, so
// no saved map changes meaning.
//
// Pure. Zero React dependency.
// ============================================================
import type { Boundary, Vec2 } from './boundary';
import { makeBoundary } from './boundary';

export interface ArenaShape {
  key: string;
  label: string;
  /** Generator shapes: ring vertex count and preset stretch. */
  sides?: number;
  scaleX?: number;
  /**
   * Irregular shapes: a unit ring at apothem `r`, centred on the origin,
   * unrotated and unscaled. `arenaBoundary` regenerates it on every edit
   * so the dimension sliders stay live for these too.
   */
  ring?: (r: number) => Vec2[];
}

/** An L filling the 2r square, the +x/+z quadrant bitten out. */
function lShapeRing(r: number): Vec2[] {
  return [
    { x: -r, z: -r },
    { x: r, z: -r },
    { x: r, z: 0 },
    { x: 0, z: 0 },
    { x: 0, z: r },
    { x: -r, z: r },
  ];
}

/** A plus in the 2r square, arms two thirds of a side wide. */
function crossRing(r: number): Vec2[] {
  const a = r / 3;
  return [
    { x: -a, z: -r },
    { x: a, z: -r },
    { x: a, z: -a },
    { x: r, z: -a },
    { x: r, z: a },
    { x: a, z: a },
    { x: a, z: r },
    { x: -a, z: r },
    { x: -a, z: a },
    { x: -r, z: a },
    { x: -r, z: -a },
    { x: -a, z: -a },
  ];
}

export const ARENA_SHAPES: ArenaShape[] = [
  { key: 'square', label: 'Square', sides: 4, scaleX: 1 },
  { key: 'rectangle', label: 'Rectangle', sides: 4, scaleX: 2 },
  { key: 'corridor', label: 'Corridor', sides: 4, scaleX: 4 },
  { key: 'hex', label: 'Hex', sides: 6, scaleX: 1 },
  { key: 'octagon', label: 'Octagon', sides: 8, scaleX: 1 },
  { key: 'circle', label: 'Circle', sides: 24, scaleX: 1 },
  { key: 'l-shape', label: 'L-shape', ring: lShapeRing },
  { key: 'cross', label: 'Cross', ring: crossRing },
  // Hand-drawn ring: `vertices` are whatever was drawn, and the
  // generator parameters are left alone.
  { key: 'custom', label: 'Custom', sides: 4, scaleX: 1 },
];

const byKey = (key: string | undefined) => ARENA_SHAPES.find((s) => s.key === key);

/**
 * What shape this boundary reads as.
 *
 * A stored discriminant wins; a bare vertices ring is custom; otherwise
 * the legacy sides+scaleX inference, with the historical fallback to
 * square for anything unrecognised.
 */
export function arenaShapeOf(boundary: Boundary): string {
  if (byKey(boundary.shape)) return boundary.shape as string;
  if (boundary.vertices?.length) return 'custom';
  const match = ARENA_SHAPES.find((s) => !s.ring && s.sides === boundary.sides && s.scaleX === boundary.scaleX);
  return match?.key ?? 'square';
}

const DEG = Math.PI / 180;

/** Scale in the shape's own frame, then rotate rigidly, then place. */
function placeRing(ring: Vec2[], b: Boundary): Vec2[] {
  const cos = Math.cos(b.rotation * DEG);
  const sin = Math.sin(b.rotation * DEG);
  return ring.map((v) => {
    const x = v.x * b.scaleX;
    const z = v.z * b.scaleZ;
    return { x: b.centre.x + x * cos - z * sin, z: b.centre.z + x * sin + z * cos };
  });
}

/**
 * THE one writer of arena geometry. Merge the patch, then make the
 * stored shape true:
 *
 * - a generator shape strips any vertices ring (the old UIs spread
 *   `...boundary`, so selecting a preset over a hand-drawn arena
 *   silently did nothing - vertices override everything);
 * - a ring shape regenerates its vertices from the current radius,
 *   stretch, rotation and centre, so the sliders stay live;
 * - custom keeps whatever ring it has.
 *
 * Note the frames differ deliberately: generator shapes keep the
 * engine's rotate-then-stretch order (saved maps depend on it), while
 * ring shapes stretch first and rotate rigidly - an L-shape should
 * turn, not shear.
 */
export function arenaBoundary(prev: Boundary, patch: Partial<Boundary>): Boundary {
  const key = byKey(patch.shape) ? (patch.shape as string) : arenaShapeOf(prev);
  const def = byKey(key) ?? ARENA_SHAPES[0];

  const merged = makeBoundary({ ...prev, ...patch, shape: key });
  if (key === 'custom') return merged;

  // Selecting a generator/ring preset applies its stretch; other edits
  // (radius, rotation...) keep the boundary's own.
  const presetPick = patch.shape !== undefined && patch.shape !== prev.shape;
  const base =
    presetPick && !def.ring
      ? makeBoundary({ ...merged, sides: def.sides ?? 4, scaleX: def.scaleX ?? 1, scaleZ: 1 })
      : merged;

  const { vertices: _dropped, ...flat } = base;
  if (def.ring) {
    const shaped = presetPick ? makeBoundary({ ...flat, scaleX: 1, scaleZ: 1 }) : makeBoundary(flat);
    return { ...shaped, vertices: placeRing(def.ring(shaped.radius), shaped) };
  }
  return makeBoundary(flat);
}
